// Description: This component displays a timeline of campaigns with options to view, edit, and manage escalations. It includes search functionality and expandable rows for detailed views.


import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import baseUrl from "../../api/universalApi";
import { useNavigate } from "react-router-dom";
import { ChevronUp } from "lucide-react";
import PaymentTracking from "./PaymentTracking"; // Assuming this is the component for payment tracking
import Escalations from "./Escalation/Escalations";
import EditCampaign from "./Escalation/EditCampaign";

const PaymentTimeline = ({ campaigns,fetchAllCampaigns }) => {
  const [popup, setPopup] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCampaigns, setFilteredCampaigns] = useState(campaigns);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedRowEsc, setExpandedRowEsc] = useState(null);

  const [expandedRowEdit, setExpandedRowEdit] = useState(null);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredCampaigns(campaigns);
    } else {
      setFilteredCampaigns(
        campaigns.filter(
          (camp) =>
            camp.campaignTitle
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            camp.brandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            camp.pocEmail.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, campaigns]);

  const handleCardClick = (campaign) => {
    setSelectedCampaign(campaign);
    setPopup(true);
  };

  const closePopup = () => {
    setPopup(false);
    setSelectedCampaign(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="p-2" style={{ fontFamily: "Gilroy-Medium" }}>
      <h2 className="text-xl font-bold mb-2">Campaigns</h2>
      <p>Manage your campaigns settings and preferences.</p>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search Campaigns"
          className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap border-gray-300 gap-2">
        <div className="overflow-x-auto w-full">
          <table className="w-full bg-white rounded-lg shadow border border-gray-200">
            <thead>
              <tr className="bg-gray-100 text-left text-sm font-medium text-gray-700">
                <th className="px-4 py-3">Campaign Title</th>
                
                <th className="px-4 py-3">Contract</th>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">POC Email</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-200">
              {filteredCampaigns.map((camp, index) => (
                <React.Fragment key={index}>
                  <tr className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-2">{camp.campaignTitle}</td>
                  
                    <td className="px-4 py-2">{camp?.contract?.rep_email ? camp?.contract?.status : "--"}</td>
                    <td className="px-4 py-2">{camp?.invoice?.rep_email ? camp?.invoice?.status : "--"}</td>
                    <td className="px-4 py-2">{camp.pocEmail}</td>
                    <td className="px-4 py-2">
                      <select
                        className="border text-black px-3 py-1 rounded-md focus:outline-none"
                        defaultValue=""
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          const value = e.target.value;
                          if (value === "view") {
                            handleCardClick(camp);
                          } else if (value === "timeline") {
                            setExpandedRow(index);
                          } else if (value === "edit") {
                            setExpandedRowEdit(index);
                          } else if (value === "escalations") {
                            setExpandedRowEsc(index);
                          }
                          e.target.value = "";
                        }}
                      >
                        <option value="" disabled>
                          take action
                        </option>
                        <option value="view">View</option>
                        <option value="edit">Edit</option>
                        <option value="timeline">Timeline</option>
                        <option value="escalations">Escalations</option>
                      </select>
                    </td>
                  </tr>

                  {expandedRow === index && (
                    <tr>
                      <td colSpan="10" className="p-0">
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "100%", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="bg-gray-100  relative"
                        >
                          <button
                            className="absolute top-2 right-4 text-gray-600 hover:text-black"
                            onClick={() => setExpandedRow(null)}
                          >
                            <ChevronUp size={18} />
                          </button>

                          {<PaymentTracking campaignDetails={camp}  fetchAllCampaigns={fetchAllCampaigns}/>}
                        </motion.div>
                      </td>
                    </tr>
                  )}

                  {expandedRowEsc === index && (
                    <tr>
                      <td colSpan="10" className="p-0">
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "100%", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="bg-gray-100  relative"
                        >
                          <button
                            className="absolute top-2 right-4 text-gray-600 hover:text-black"
                            onClick={() => setExpandedRowEsc(null)}
                          >
                            <ChevronUp size={18} />
                          </button>

                          {<Escalations campaignId={camp._id} />}
                        </motion.div>
                      </td>
                    </tr>
                  )}

                  {expandedRowEdit === index && (
                    <tr>
                      <td colSpan="10" className="p-0">
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "100%", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="bg-gray-100  relative"
                        >
                          <button
                            className="absolute top-2 right-4 text-gray-600 hover:text-black"
                            onClick={() => setExpandedRowEdit(null)}
                          >
                            <ChevronUp size={18} />
                          </button>

                          {<EditCampaign campaignDetails={camp} fetchAllCampaigns={fetchAllCampaigns} />}
                        </motion.div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {popup && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl w-11/12 md:w-2/3 lg:w-1/2 relative">
            <button
              onClick={closePopup}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              &#x2715;
            </button>
            <h2 className="text-xl font-bold mb-2">
              {selectedCampaign.campaignTitle}
            </h2>
            <div className="space-y-1 text-sm">
              <p>
                <strong>Brand:</strong> {selectedCampaign.brandName}
              </p>
              <p>
                <strong>Comments:</strong> {selectedCampaign.comments}
              </p>
              <p>
                <strong>Content Link:</strong> {selectedCampaign.contentLink}
              </p>
              <p>
                <strong>Created At:</strong>{" "}
                {formatDate(selectedCampaign.createdAt)}
              </p>
              <p>
                <strong>Creator ID:</strong> {selectedCampaign.creatorId}
              </p>
              <p>
                <strong>Platform:</strong> {selectedCampaign.platform}
              </p>
              <p>
                <strong>POC Email:</strong> {selectedCampaign.pocEmail}
              </p>
              <p>
                <strong>Status:</strong> {selectedCampaign.status}
              </p>
              <p>
                <strong>Updated At:</strong>{" "}
                {formatDate(selectedCampaign.updatedAt)}
              </p>

              {selectedCampaign.vrfFile && (
                <>
                  <h3 className="mt-4 font-semibold">VRF File</h3>
                  <p>
                    <strong>Aadhar:</strong>{" "}
                    {selectedCampaign.vrfFile.aadhar_card}
                  </p>
                  <p>
                    <strong>Creator Name:</strong>{" "}
                    {selectedCampaign.vrfFile.creator_name}
                  </p>
                  <p>
                    <strong>Emergency Contact:</strong>{" "}
                    {selectedCampaign.vrfFile.emergency_poc_contact}
                  </p>
                  <p>
                    <strong>GST:</strong> {selectedCampaign.vrfFile.gst}
                  </p>
                  <p>
                    <strong>PAN:</strong> {selectedCampaign.vrfFile.panCard}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentTimeline;
